import { useMemo, useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Id, Datum, Uhrzeit, EinsatzEntwurf, Unterrichtsform, EinsatzStatus } from '../daten/typen';
import { dozentenLage, freieRaeume, kursFenster, pruefeEinsatz, fehlerText, sollIst, kinderVon, naechsterOffenerKnoten, knotenPfad, fachbereichsKnoten } from '../daten/regeln';
import { tagLang, deLang, kurzeZeit, minuten } from '../daten/zeit';
import { Seitenpanel, Knopf, Feld, Icon, Kuerzel, dozentName } from '../ui/bausteine';

export interface PanelZiel { kursId: Id; datum: Datum; beginn: Uhrzeit; ende: Uhrzeit; einsatzId?: Id; knotenVorschlag?: Id | null; dozentVorschlag?: Id | null }

export function EinsatzPanel({ ziel, schliessen }: { ziel: PanelZiel | null; schliessen: () => void }) {
  return (
    <Seitenpanel offen={!!ziel} schliessen={schliessen}
      titel={ziel ? (ziel.einsatzId ? 'Einsatz bearbeiten' : 'Einsatz planen') : ''}
      untertitel={ziel ? `${tagLang(ziel.datum)}, ${deLang(ziel.datum)}` : ''}>
      {ziel && <Formular key={`${ziel.kursId}-${ziel.datum}-${ziel.beginn}-${ziel.einsatzId ?? ''}`} ziel={ziel} schliessen={schliessen} />}
    </Seitenpanel>
  );
}

function Formular({ ziel, schliessen }: { ziel: PanelZiel; schliessen: () => void }) {
  const { stand, quelle, ausfuehren, fragen } = useDaten();
  const kurs = stand.kurse.find(k => k.id === ziel.kursId)!;
  const alt = ziel.einsatzId ? stand.einsaetze.find(e => e.id === ziel.einsatzId) : undefined;
  const fenster = kursFenster(stand, kurs);

  const startKnoten = alt?.kurs_knoten_id ?? ziel.knotenVorschlag ?? naechsterOffenerKnoten(stand, kurs.id)?.id ?? null;
  const [knotenId, setKnotenId] = useState<Id | null>(startKnoten);
  const [beginn, setBeginn] = useState<Uhrzeit>(alt?.beginn ?? ziel.beginn);
  const [ende, setEnde] = useState<Uhrzeit>(alt?.ende ?? ziel.ende);
  const [form, setForm] = useState<Unterrichtsform>(alt?.form ?? kurs.standard_form);
  const [status, setStatus] = useState<EinsatzStatus>(alt?.status ?? 'entwurf');
  const [hinweis, setHinweis] = useState(alt?.hinweis ?? '');
  const [zeigeAlle, setZeigeAlle] = useState(false);

  const lage = useMemo(() => dozentenLage(stand, kurs.id, knotenId, ziel.datum, beginn, ende, alt?.id), [stand, kurs.id, knotenId, ziel.datum, beginn, ende, alt?.id]);
  const standardDoz = lage.find(l => l.standard && l.frei && l.qualifiziert);
  const [dozentId, setDozentId] = useState<Id | null>(alt ? alt.dozent_id : (ziel.dozentVorschlag ?? standardDoz?.dozent.id ?? lage.find(l => l.frei && l.qualifiziert)?.dozent.id ?? null));

  const raeume = useMemo(() => freieRaeume(stand, kurs.standort_id, ziel.datum, beginn, ende, alt?.id), [stand, kurs.standort_id, ziel.datum, beginn, ende, alt?.id]);
  const startRaum = alt ? alt.raum_id : (raeume.find(r => r.raum.id === kurs.standard_raum_id && r.frei)?.raum.id ?? raeume.find(r => r.frei)?.raum.id ?? null);
  const [raumId, setRaumId] = useState<Id | null>(startRaum);

  const entwurf: EinsatzEntwurf = {
    id: alt?.id, kurs_id: kurs.id, kurs_knoten_id: knotenId, dozent_id: dozentId, raum_id: form === 'online' ? null : raumId,
    form, datum: ziel.datum, beginn, ende, ue: alt?.ue ?? 4, status, hinweis: hinweis.trim() || null,
  };
  const verstoss = pruefeEinsatz(stand, entwurf);

  // Lehrplan-Auswahl: Blätter gruppiert nach Fachbereich mit Restbedarf
  const si = sollIst(stand, kurs.id);
  const knoten = stand.kursKnoten.filter(k => k.kurs_id === kurs.id);
  const kinder = kinderVon(knoten);
  const gruppen: { titel: string; optionen: { id: Id; text: string }[] }[] = [];
  const fbWurzeln = knoten.filter(k => k.fachbereich_id && !knoten.some(p => p.id === k.parent_id && p.fachbereich_id));
  for (const fb of fbWurzeln.sort((a, b) => (a.parent_id ?? '').localeCompare(b.parent_id ?? '') || a.sortierung - b.sortierung)) {
    const opt: { id: Id; text: string }[] = [];
    const lauf = (pid: Id, praefix: string) => {
      for (const k of kinder.get(pid) ?? []) {
        if (kinder.has(k.id)) lauf(k.id, `${praefix}${k.bezeichnung} · `);
        else { const w = si.get(k.id); opt.push({ id: k.id, text: `${praefix}${k.bezeichnung}  (${w?.geplant ?? 0}/${w?.soll ?? 0} UE)` }); }
      }
    };
    lauf(fb.id, '');
    if (!kinder.has(fb.id)) { const w = si.get(fb.id); opt.push({ id: fb.id, text: `${fb.bezeichnung}  (${w?.geplant ?? 0}/${w?.soll ?? 0} UE)` }); }
    gruppen.push({ titel: fb.bezeichnung, optionen: opt });
  }
  const fbK = fachbereichsKnoten(stand, knotenId);
  const fbFarbe = stand.fachbereiche.find(f => f.id === fbK?.fachbereich_id)?.farbe ?? 'var(--mm-linie)';

  const verfuegbar = lage.filter(l => l.frei && l.qualifiziert);
  const nicht = lage.filter(l => !(l.frei && l.qualifiziert) && (zeigeAlle || l.qualifiziert));
  const dauer = minuten(ende) - minuten(beginn);

  async function speichern(freigeben = false) {
    const ok = await ausfuehren(() => quelle.einsatzSpeichern({ ...entwurf, status: freigeben ? 'freigegeben' : entwurf.status }),
      freigeben ? 'Einsatz gespeichert und freigegeben' : 'Einsatz gespeichert');
    if (ok) schliessen();
  }

  return (
    <div className="formular">
      <div className="panel-kurs" style={{ borderColor: kurs.farbe }}>
        <strong>{kurs.kennung}</strong> <span>{kurs.bezeichnung}</span>
      </div>

      <Feld label="Zeit">
        <div className="chips">
          {fenster.map(f => (
            <button key={f.id} type="button" className={`chip ${beginn === f.beginn && ende === f.ende ? 'an' : ''}`}
              onClick={() => { setBeginn(f.beginn); setEnde(f.ende); }}>{f.bezeichnung} · {kurzeZeit(f.beginn)}–{kurzeZeit(f.ende)}</button>
          ))}
        </div>
        <div className="zeile">
          <input type="time" value={beginn} onChange={e => setBeginn(e.target.value)} aria-label="Beginn" />
          <span>bis</span>
          <input type="time" value={ende} onChange={e => setEnde(e.target.value)} aria-label="Ende" />
          <span className="leise">{dauer > 0 ? `${Math.floor(dauer / 60)}:${String(dauer % 60).padStart(2, '0')} h · 4 UE` : ''}</span>
        </div>
      </Feld>

      <Feld label="Thema im Lehrplan">
        <div className="mit-farbe" style={{ borderColor: fbFarbe }}>
          <select value={knotenId ?? ''} onChange={e => setKnotenId(e.target.value || null)}>
            <option value="">– ohne Zuordnung –</option>
            {gruppen.map(g => (
              <optgroup key={g.titel} label={g.titel}>
                {g.optionen.map(o => <option key={o.id} value={o.id}>{o.text}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
      </Feld>

      <Feld label="Form">
        <div className="segment">
          <button type="button" className={form === 'praesenz' ? 'an' : ''} onClick={() => setForm('praesenz')}><Icon name="raum" /> Präsenz</button>
          <button type="button" className={form === 'online' ? 'an' : ''} onClick={() => setForm('online')}><Icon name="online" /> Online</button>
        </div>
      </Feld>

      {form === 'praesenz' && (
        <Feld label="Raum">
          <select value={raumId ?? ''} onChange={e => setRaumId(e.target.value || null)}>
            <option value="">– kein Raum –</option>
            {raeume.map(r => <option key={r.raum.id} value={r.raum.id} disabled={!r.frei}>{r.raum.bezeichnung}{r.raum.plaetze ? ` (${r.raum.plaetze} Plätze)` : ''}{r.frei ? '' : ' – belegt'}</option>)}
          </select>
        </Feld>
      )}

      <Feld label={`Dozent · ${verfuegbar.length} verfügbar`}>
        <div className="dozentenwahl" role="radiogroup">
          {verfuegbar.map(l => (
            <button key={l.dozent.id} type="button" role="radio" aria-checked={dozentId === l.dozent.id}
              className={`dozent-option ${dozentId === l.dozent.id ? 'an' : ''}`} onClick={() => setDozentId(l.dozent.id)}>
              <Kuerzel text={l.dozent.kuerzel} farbe={l.dozent.farbe} />
              <span className="dozent-text"><strong>{dozentName(l.dozent)}</strong>
                <small>{l.standard ? 'Standard für diesen Fachbereich · ' : ''}{l.ueWoche} UE diese Woche · {l.dozent.beschaeftigung === 'honorar' ? 'Honorar' : 'angestellt'}</small></span>
              {l.standard && <Icon name="stern" titel="Standard-Dozent" />}
            </button>
          ))}
          <button type="button" role="radio" aria-checked={dozentId === null}
            className={`dozent-option offen ${dozentId === null ? 'an' : ''}`} onClick={() => setDozentId(null)}>
            <span className="kuerzel" style={{ background: 'var(--mm-warn)' }}>?</span>
            <span className="dozent-text"><strong>Noch offen</strong><small>Termin ohne Dozent anlegen</small></span>
          </button>
          {nicht.length > 0 && <div className="trenner">Nicht verfügbar</div>}
          {nicht.map(l => (
            <div key={l.dozent.id} className={`dozent-option gesperrt ${dozentId === l.dozent.id ? 'an' : ''}`} aria-disabled="true">
              <Kuerzel text={l.dozent.kuerzel} farbe="var(--mm-text-3)" />
              <span className="dozent-text"><strong>{dozentName(l.dozent)}</strong><small>{l.grund}</small></span>
            </div>
          ))}
          <button type="button" className="link" onClick={() => setZeigeAlle(!zeigeAlle)}>
            {zeigeAlle ? 'Nur Dozenten dieses Fachbereichs zeigen' : 'Auch Dozenten anderer Fachbereiche zeigen'}
          </button>
        </div>
      </Feld>

      <Feld label="Hinweis für den Stundenplan" hinweis="Erscheint für die Teilnehmenden in Moodle, z. B. „Ersatz für den 01.10.“">
        <input type="text" value={hinweis} onChange={e => setHinweis(e.target.value)} maxLength={140} />
      </Feld>

      {alt && (
        <Feld label="Status">
          <div className="segment">
            <button type="button" className={status === 'entwurf' ? 'an' : ''} onClick={() => setStatus('entwurf')}>Entwurf</button>
            <button type="button" className={status === 'freigegeben' ? 'an' : ''} onClick={() => setStatus('freigegeben')}>Freigegeben</button>
            <button type="button" className={status === 'entfaellt' ? 'an' : ''} onClick={() => setStatus('entfaellt')}>Entfällt</button>
          </div>
        </Feld>
      )}

      {verstoss && <div className="hinweisbox fehler"><Icon name="warnung" /> {fehlerText(verstoss)}</div>}
      {!verstoss && dozentId === null && status !== 'entfaellt' && <div className="hinweisbox warn"><Icon name="warnung" /> Der Termin ist ohne Dozent und erscheint als „Vertretung gesucht“.</div>}

      <div className="knopfleiste">
        <Knopf art="primaer" icon="haken" disabled={!!verstoss} onClick={() => speichern(false)}>Speichern</Knopf>
        {(!alt || alt.status === 'entwurf') && status === 'entwurf' &&
          <Knopf icon="senden" disabled={!!verstoss} onClick={() => speichern(true)}>Speichern und freigeben</Knopf>}
        {alt && <Knopf art="gefahr" icon="muell" onClick={async () => {
          if (alt.status === 'freigegeben' && !await fragen('Der Termin ist bereits freigegeben. Wirklich löschen?\nAlternativ den Status „Entfällt“ wählen – dann sehen die Teilnehmenden den Hinweis im Stundenplan.', 'Endgültig löschen', true)) return;
          if (await ausfuehren(() => quelle.einsatzLoeschen(alt.id), 'Einsatz gelöscht')) schliessen();
        }}>Löschen</Knopf>}
      </div>
      {alt && <p className="leise klein">Pfad: {knotenPfad(stand, alt.kurs_knoten_id).map(k => k.bezeichnung).join(' › ') || '–'}</p>}
    </div>
  );
}
